const DEFAULT_PROD_API_BASE = 'https://drishti-business-os-production.up.railway.app/api';
const rawBaseUrl = import.meta.env.VITE_API_BASE_URL || DEFAULT_PROD_API_BASE;
const API_BASE = rawBaseUrl.endsWith('/') ? rawBaseUrl.slice(0, -1) : rawBaseUrl;

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  details?: any;
  grouped?: any;
  summary?: any;
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  results?: T;
  unreadCount?: number;
  attendance?: any;
}

export class ApiClient {
  public static getAccessToken(): string | null {
    const token = localStorage.getItem('drishti_access_token');
    if (token === 'mock_admin_access_token') {
      localStorage.removeItem('drishti_access_token');
      localStorage.removeItem('drishti_refresh_token');
      return null;
    }
    return token;
  }

  public static getRefreshToken(): string | null {
    const token = localStorage.getItem('drishti_refresh_token');
    if (token === 'mock_admin_refresh_token') {
      localStorage.removeItem('drishti_access_token');
      localStorage.removeItem('drishti_refresh_token');
      return null;
    }
    return token;
  }

  public static setTokens(accessToken: string, refreshToken?: string) {
    localStorage.setItem('drishti_access_token', accessToken);
    if (refreshToken) {
      localStorage.setItem('drishti_refresh_token', refreshToken);
    }
  }

  public static clearTokens() {
    localStorage.removeItem('drishti_access_token');
    localStorage.removeItem('drishti_refresh_token');
  }

  public static async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const token = this.getAccessToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Ensure clean endpoint path concatenation
    const targetUrl = endpoint.startsWith('/')
      ? `${API_BASE}${endpoint}`
      : `${API_BASE}/${endpoint}`;

    const method = (options.method || 'GET').toUpperCase();

    // Pre-flight console logging for audit and debugging
    console.log(`[API Request] ${method} ${targetUrl}`);
    if (options.body) {
      try {
        console.log('Body:', JSON.parse(options.body as string));
      } catch (e) {
        console.log('Body:', options.body);
      }
    }

    try {
      let response = await fetch(targetUrl, {
        ...options,
        headers,
      });

      // Handle 401 Unauthorized (attempt automatic token refresh)
      if (response.status === 401 && !endpoint.includes('/auth/login') && !endpoint.includes('/auth/refresh')) {
        const refreshToken = this.getRefreshToken();
        if (refreshToken) {
          console.log('[ApiClient] Access token expired or invalid. Attempting token refresh...');
          const refreshRes = await fetch(`${API_BASE}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
          });

          if (refreshRes.ok) {
            const refreshText = await refreshRes.text();
            let refreshData: any = {};
            try { refreshData = JSON.parse(refreshText); } catch (e) {}

            if (refreshData.success && refreshData.data?.accessToken) {
              console.log('[ApiClient] Token refresh succeeded. Retrying original request.');
              this.setTokens(refreshData.data.accessToken);
              headers['Authorization'] = `Bearer ${refreshData.data.accessToken}`;

              // Retry original request with new access token
              response = await fetch(targetUrl, {
                ...options,
                headers,
              });
            } else {
              console.log('[ApiClient] Refresh token rejected. Redirecting to login.');
              this.clearTokens();
              if (window.location.pathname !== '/login') {
                window.location.href = '/login';
              }
            }
          } else {
            console.log('[ApiClient] Refresh endpoint returned non-200. Redirecting to login.');
            this.clearTokens();
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
        } else {
          this.clearTokens();
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      }

      const text = await response.text();
      let json: any;
      try {
        json = text ? JSON.parse(text) : {};
      } catch (parseErr) {
        console.error('Failed to parse API response as JSON:', text);
        let friendlyError: string;
        switch (response.status) {
          case 400: friendlyError = 'Invalid request. Please check the data and try again.'; break;
          case 401: friendlyError = 'Session expired. Please log in again.'; break;
          case 403: friendlyError = 'You do not have permission to perform this action.'; break;
          case 404: friendlyError = 'The requested record could not be found. It may have already been deleted.'; break;
          case 409: friendlyError = 'This record cannot be deleted because it is referenced elsewhere.'; break;
          case 422: friendlyError = 'The request could not be processed due to invalid data.'; break;
          case 500: friendlyError = 'An internal server error occurred. Please try again or contact support.'; break;
          case 502:
          case 503:
          case 504: friendlyError = 'The server is temporarily unavailable. Please try again shortly.'; break;
          default:  friendlyError = `Request failed with status ${response.status}. Please try again.`;
        }
        return {
          success: false,
          error: friendlyError,
        };
      }

      // If response is not ok and JSON has error message, ensure success: false
      if (!response.ok && json.success !== false) {
        json.success = false;
        if (!json.error) {
          if (response.status === 401) json.error = 'Unauthorized access. Please log in again.';
          else if (response.status === 403) json.error = 'Access forbidden. Insufficient permissions.';
          else if (response.status === 404) json.error = 'Requested resource not found.';
          else if (response.status >= 500) json.error = 'Internal server error. Please try again later.';
          else json.error = `HTTP Request failed with status ${response.status}`;
        }
      }

      return json;
    } catch (err: any) {
      console.error('API Client network error:', err);
      const isFetchError = err?.name === 'TypeError' || err?.message === 'Failed to fetch';
      return {
        success: false,
        error: isFetchError
          ? `Network connection error. Server unavailable or API URL is unreachable. (${targetUrl})`
          : (err.message || 'Network request failed. Is the backend server online?'),
      };
    }
  }

  public static get<T = any>(endpoint: string) {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  public static post<T = any>(endpoint: string, body?: any) {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  public static put<T = any>(endpoint: string, body?: any) {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  public static patch<T = any>(endpoint: string, body?: any) {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  public static delete<T = any>(endpoint: string, body?: any) {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
  }
}
